// FORFEO INC. — Lot 4R-J
// Contrat pur pour le centre d'intégrations, la fiabilité et les portes de production.
// Reserve demeure l'autorité de disponibilité/réservation.
// Giftshop demeure l'autorité exclusive des prix, paiements, remboursements, passes et QR.

export const FORFEO_RESERVE_PRODUCTION_SCHEMA =
  'forfeo_reserve_production_v1' as const;

export const FORFEO_RESERVE_PARTNER_MANAGEMENT_COMMANDS = [
  'create_application',
  'update_branding',
  'add_origin',
  'remove_origin',
  'rotate_server_key',
  'revoke_server_key',
  'activate_application',
  'suspend_application',
  'create_webhook',
  'pause_webhook',
  'resume_webhook',
  'run_smoke_test',
] as const;

export const FORFEO_RESERVE_PRODUCTION_CHECKS = [
  'foundations',
  'expired_holds',
  'capacity_conflicts',
  'payments_without_bookings',
  'bookings_without_payment',
  'failed_booking_commands',
  'partner_dead_letters',
  'partner_origins',
  'partner_keys',
  'reserve_feature_flags',
  'giftshop_health',
  'synthetic_booking',
  'synthetic_financial_flow',
  'rollback_readiness',
] as const;

export const FORFEO_RESERVE_PRODUCTION_REQUIRED_FLAGS = [
  'VITE_FORFEO_RESERVE_BOOKING_V1',
  'VITE_FORFEO_RESERVE_CALENDAR_V1',
  'VITE_FORFEO_RESERVE_POLICIES_V1',
  'VITE_FORFEO_RESERVE_MERCHANT_PORTAL_V1',
  'VITE_FORFEO_RESERVE_USER_EXPERIENCE_V1',
  'VITE_FORFEO_RESERVE_INTEGRATIONS_V1',
  'VITE_FORFEO_RESERVE_VIRTUAL_TOUR_V1',
] as const;

export type ForfeoReservePartnerManagementCommandKind =
  typeof FORFEO_RESERVE_PARTNER_MANAGEMENT_COMMANDS[number];

export type ForfeoReserveProductionCheckKind =
  typeof FORFEO_RESERVE_PRODUCTION_CHECKS[number];

export type ForfeoReserveIntegrationStatus =
  | 'active'
  | 'suspended'
  | 'revoked';

export type ForfeoReserveProductionCheckState =
  | 'pass'
  | 'warn'
  | 'fail'
  | 'not_run';

export interface ForfeoReservePartnerOrigin {
  id: string;
  origin: string;
  verificationStatus: 'pending' | 'verified' | 'failed';
  verifiedAt?: string | null;
  createdAt: string;
}

export interface ForfeoReservePartnerApiKey {
  id: string;
  prefix: string;
  status: 'active' | 'revoked' | 'expired';
  expiresAt?: string | null;
  lastUsedAt?: string | null;
  createdAt: string;
}

export interface ForfeoReservePartnerWebhook {
  id: string;
  endpointUrl: string;
  status: 'active' | 'paused' | 'revoked';
  subscribedEvents: string[];
  deliveredCount: number;
  pendingCount: number;
  deadLetterCount: number;
  createdAt: string;
}

export interface ForfeoReservePartnerApplication {
  id: string;
  businessId: string;
  name: string;
  status: ForfeoReserveIntegrationStatus;
  planCode: string;
  billingTier: 'included' | 'premium' | 'enterprise';
  installationStatus:
    | 'not_started'
    | 'code_generated'
    | 'installed'
    | 'verified';
  publicWidgetKey: string;
  accentColor?: string | null;
  whiteLabelEnabled: boolean;
  hourlyQuota: number;
  dailyQuota: number;
  origins: ForfeoReservePartnerOrigin[];
  apiKeys: ForfeoReservePartnerApiKey[];
  webhooks: ForfeoReservePartnerWebhook[];
  usageHour: number;
  usageDay: number;
  lastSmokeTestStatus?: 'pass' | 'fail' | 'not_run';
  lastSmokeTestAt?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface ForfeoReserveMerchantIntegrationSnapshot {
  schema: typeof FORFEO_RESERVE_PRODUCTION_SCHEMA;
  authority: 'RESERVE';
  businessId: string;
  applications: ForfeoReservePartnerApplication[];
  entitlements: {
    maximumApplications: number;
    maximumDomainsPerApplication: number;
    apiEnabled: boolean;
    webhooksEnabled: boolean;
    whiteLabelEnabled: boolean;
    installationServiceAvailable: boolean;
  };
  generatedAt: string;
}

export interface ForfeoReserveAdminIntegrationSnapshot {
  schema: typeof FORFEO_RESERVE_PRODUCTION_SCHEMA;
  authority: 'RESERVE';
  totals: {
    applications: number;
    activeApplications: number;
    suspendedApplications: number;
    verifiedDomains: number;
    activeApiKeys: number;
    pendingWebhookDeliveries: number;
    deadLetterDeliveries: number;
  };
  applications: ForfeoReservePartnerApplication[];
  alerts: Array<{
    id: string;
    severity: 'info' | 'warning' | 'critical';
    code: string;
    businessId?: string | null;
    applicationId?: string | null;
    message: string;
    status: 'open' | 'acknowledged' | 'resolved';
    createdAt: string;
  }>;
  generatedAt: string;
}

export interface ForfeoReserveProductionReadinessCheck {
  kind: ForfeoReserveProductionCheckKind;
  state: ForfeoReserveProductionCheckState;
  message: string;
  observedValue?: number | string | boolean | null;
  threshold?: number | string | boolean | null;
}

export interface ForfeoReserveProductionReadinessSnapshot {
  schema: typeof FORFEO_RESERVE_PRODUCTION_SCHEMA;
  authority: 'RESERVE';
  environment: 'local' | 'staging' | 'production';
  overallState: 'ready' | 'blocked' | 'degraded';
  checks: ForfeoReserveProductionReadinessCheck[];
  blockers: string[];
  featureFlags: Record<string, boolean>;
  remoteOperationsExecuted: false;
  generatedAt: string;
}

export interface ForfeoReservePartnerManagementCommand {
  kind: ForfeoReservePartnerManagementCommandKind;
  businessId: string;
  applicationId?: string;
  apiKeyId?: string;
  webhookId?: string;
  name?: string;
  planCode?: string;
  billingTier?: 'included' | 'premium' | 'enterprise';
  accentColor?: string;
  whiteLabelEnabled?: boolean;
  origin?: string;
  endpointUrl?: string;
  subscribedEvents?: string[];
  idempotencyKey: string;
  correlationId: string;
}

export const FORFEO_RESERVE_PRODUCTION_AUTHORITIES = {
  availability: 'RESERVE',
  capacity: 'RESERVE',
  holds: 'RESERVE',
  bookings: 'RESERVE',
  policies: 'RESERVE',
  partnerApplications: 'RESERVE',
  partnerDomains: 'RESERVE',
  partnerQuotas: 'RESERVE',
  prices: 'GIFTSHOP',
  taxes: 'GIFTSHOP',
  fees: 'GIFTSHOP',
  payments: 'GIFTSHOP',
  refunds: 'GIFTSHOP',
  passes: 'GIFTSHOP',
  qr: 'GIFTSHOP',
  browser: 'NONE',
} as const;

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const financialFields = new Set([
  'amount',
  'amountCents',
  'amount_cents',
  'price',
  'priceId',
  'price_id',
  'tax',
  'taxes',
  'fee',
  'fees',
  'deposit',
  'currency',
  'stripe',
  'stripeId',
  'stripe_id',
  'paymentIntent',
  'payment_intent',
  'checkoutSession',
  'checkout_session',
  'qrSecret',
  'qr_secret',
]);

export function assertNoReserveProductionFinancialAuthority(
  value: unknown,
  path = 'payload',
): void {
  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      assertNoReserveProductionFinancialAuthority(entry, `${path}[${index}]`)
    );
    return;
  }
  if (!value || typeof value !== 'object') return;

  for (const [key, child] of Object.entries(
    value as Record<string, unknown>,
  )) {
    if (financialFields.has(key)) {
      throw new Error(`forbidden_financial_field:${path}.${key}`);
    }
    assertNoReserveProductionFinancialAuthority(child, `${path}.${key}`);
  }
}

export function normalizeReservePartnerOrigin(value: string): string {
  const url = new URL(value.trim());
  if (url.username || url.password || url.pathname !== '/' || url.search || url.hash) {
    throw new Error('invalid_partner_origin');
  }
  if (url.protocol !== 'https:' && url.hostname !== 'localhost') {
    throw new Error('partner_origin_requires_https');
  }
  return url.origin;
}

export function validateReservePartnerManagementCommand(
  command: ForfeoReservePartnerManagementCommand,
): string[] {
  const errors: string[] = [];

  if (!FORFEO_RESERVE_PARTNER_MANAGEMENT_COMMANDS.includes(command.kind)) {
    errors.push('invalid_kind');
  }
  if (!uuidPattern.test(command.businessId)) errors.push('invalid_business_id');
  if (command.applicationId && !uuidPattern.test(command.applicationId)) {
    errors.push('invalid_application_id');
  }
  if (command.apiKeyId && !uuidPattern.test(command.apiKeyId)) {
    errors.push('invalid_api_key_id');
  }
  if (command.webhookId && !uuidPattern.test(command.webhookId)) {
    errors.push('invalid_webhook_id');
  }
  if (
    command.name !== undefined
    && (command.name.trim().length < 2 || command.name.length > 120)
  ) {
    errors.push('invalid_name');
  }
  if (
    command.accentColor !== undefined
    && !/^#[0-9a-fA-F]{6}$/.test(command.accentColor)
  ) {
    errors.push('invalid_accent_color');
  }
  if (command.origin !== undefined) {
    try {
      normalizeReservePartnerOrigin(command.origin);
    } catch {
      errors.push('invalid_origin');
    }
  }
  if (
    command.endpointUrl !== undefined
    && !/^https:\/\//i.test(command.endpointUrl)
  ) {
    errors.push('invalid_webhook_url');
  }
  if (
    command.subscribedEvents
    && command.subscribedEvents.some((event) =>
      ![
        'reservation.confirmed',
        'reservation.modified',
        'reservation.cancelled',
      ].includes(event)
    )
  ) {
    errors.push('invalid_webhook_event');
  }
  if (
    typeof command.idempotencyKey !== 'string'
    || command.idempotencyKey.trim().length < 16
    || command.idempotencyKey.length > 160
  ) {
    errors.push('invalid_idempotency_key');
  }
  if (
    typeof command.correlationId !== 'string'
    || command.correlationId.trim().length < 8
    || command.correlationId.length > 200
  ) {
    errors.push('invalid_correlation_id');
  }

  try {
    assertNoReserveProductionFinancialAuthority(command);
  } catch {
    errors.push('financial_authority_forbidden');
  }

  return [...new Set(errors)];
}
